// src/api/client.ts
import axios from 'axios';
import type { Persona, ChatResponse, Conversation, Message } from '../types';

const BASE = '/api/v1';

const api = axios.create({ baseURL: BASE });

// Attach JWT token to every request
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

// Auto-logout on 401
api.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('activePersona');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// ── Auth ──────────────────────────────────────────────────────────────────
export const authLogin = async (username: string, password: string) => {
  const form = new URLSearchParams();
  form.append('username', username);
  form.append('password', password);
  const { data } = await api.post('/auth/login', form, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });
  return data;
};

export const authRegister = async (payload: {
  name: string; email: string; username: string; password: string;
}) => {
  const { data } = await api.post('/auth/register', payload);
  return data;
};

// ── Personas ──────────────────────────────────────────────────────────────
export const fetchPersonas = async (): Promise<Persona[]> => {
  const { data } = await api.get('/personas');
  return data;
};

// ── Chat ──────────────────────────────────────────────────────────────────
export const sendChat = async (payload: {
  message: string;
  session_id: string;
  conversation_id: string | null;
  active_persona: string;
}): Promise<ChatResponse> => {
  const { data } = await api.post('/chat', payload);
  return data;
};

// ── Feedback ──────────────────────────────────────────────────────────────
export const sendFeedback = async (point_id: string, rating: 1 | -1) => {
  await api.post('/feedback', { point_id, rating });
};

// ── Conversations ─────────────────────────────────────────────────────────
export const fetchConversations = async (personaId: string = ''): Promise<Conversation[]> => {
  const { data } = await api.get(`/conversations${personaId ? '?persona=' + personaId : ''}`);
  return data;
};

export const fetchConversation = async (id: string): Promise<{ messages: Message[]; title: string }> => {
  const { data } = await api.get(`/conversations/${id}`);
  return data;
};

export const deleteConversation = async (id: string) => {
  await api.delete(`/conversations/${id}`);
};

// ── Health ────────────────────────────────────────────────────────────────
export const fetchHealth = async () => {
  const { data } = await api.get('/health');
  return data;
};
